import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const SDK_SCRIPT_ATTR = 'data-onlyoffice-sdk-loaded-from';

function loadSdk(docServerUrl) {
    return new Promise((resolve, reject) => {
        const sdkSrc = `${docServerUrl.replace(/\/$/, '')}/web-apps/apps/api/documents/api.js`;
        // Re-use SDK if already loaded from the same origin
        const existing = document.querySelector(`script[${SDK_SCRIPT_ATTR}="${docServerUrl}"]`);
        if (existing && window.DocsAPI) { resolve(window.DocsAPI); return; }
        // Remove stale SDK from different origin
        document.querySelectorAll(`script[${SDK_SCRIPT_ATTR}]`).forEach((s) => s.remove());
        const script = document.createElement('script');
        script.src = sdkSrc;
        script.setAttribute(SDK_SCRIPT_ATTR, docServerUrl);
        script.onload = () => {
            if (window.DocsAPI) resolve(window.DocsAPI);
            else reject(new Error('DocsAPI not exposed after SDK load'));
        };
        script.onerror = () => reject(new Error(`Gagal memuat SDK OnlyOffice dari ${docServerUrl}`));
        document.body.appendChild(script);
    });
}

let placeholderCounter = 0;

export default function OnlyOfficeEditorModal({ fileId, open, onClose }) {
    const containerRef = useRef(null);
    const editorRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [placeholderId] = useState(() => `onlyoffice-editor-${++placeholderCounter}`);

    useEffect(() => {
        if (!open || !fileId) return undefined;
        let cancelled = false;

        (async () => {
            setLoading(true); setError(null);
            try {
                const res = await api.get(`/onlyoffice/editor-config/${fileId}`);
                if (cancelled) return;
                const { doc_server_url, config } = res.data;
                const DocsAPI = await loadSdk(doc_server_url);
                if (cancelled) return;
                if (!DocsAPI) throw new Error('OnlyOffice SDK tidak tersedia');
                editorRef.current = new DocsAPI.DocEditor(placeholderId, {
                    ...config,
                    width: '100%',
                    height: '100%',
                    events: {
                        onDocumentStateChange: (e) => {
                            // Could surface dirty-state to user
                        },
                        onRequestClose: onClose,
                    },
                });
                setLoading(false);
            } catch (e) {
                if (cancelled) return;
                const msg = e.response?.data?.detail || e.message || 'Gagal membuka OnlyOffice';
                setError(msg);
                setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            if (editorRef.current) {
                try { editorRef.current.destroyEditor(); } catch (e) { /* noop */ }
                editorRef.current = null;
            }
        };
    }, [open, fileId, onClose, placeholderId]);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-[95vw] w-[95vw] h-[92vh] p-0 flex flex-col" data-testid="onlyoffice-editor-modal">
                <DialogHeader className="px-4 py-2 border-b">
                    <DialogTitle>Edit dengan OnlyOffice</DialogTitle>
                </DialogHeader>
                <div className="flex-1 relative bg-gray-100">
                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
                            <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                        </div>
                    )}
                    {error && (
                        <div className="absolute inset-0 flex items-center justify-center p-8">
                            <div className="text-center max-w-md">
                                <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-amber-500" />
                                <div className="font-semibold mb-1">Tidak dapat membuka editor</div>
                                <div className="text-sm text-brand-text-muted">{error}</div>
                            </div>
                        </div>
                    )}
                    <div ref={containerRef} id={placeholderId} className="w-full h-full" />
                </div>
            </DialogContent>
        </Dialog>
    );
}
